import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BaseDashboard from '../components/BaseDashboard';
import RenewalModal from '../components/RenewalModal';
import DragDropFileUpload from '../components/DragDropFileUpload';
import { createApiUrl } from '../utils/apiConfig';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import { restrictedVideoProps } from '../utils/restrictedVideo';

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [workshopPlans, setWorkshopPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // File handlers
  const handleFileUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleFileRemove = () => {
    setUploadedFiles([]);
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'lessons', label: 'Lessons' },
    { id: 'workshops', label: 'Workshops' },
    { id: 'subscriptions', label: 'Subscriptions' }
  ];

  const fetchData = async () => {
    try {
      const response = await axios.get(createApiUrl('/api/dashboard'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      
      const { user, lessons, workshopPlans, subscriptions } = response.data;
      
      setUser(user);
      setAssets(lessons || []);
      setWorkshopPlans(workshopPlans || []);
      setSubscriptions(subscriptions || []);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching user dashboard data:', error);
      console.error('Error response:', error.response?.data);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleVideoClick = (asset) => {
    setAssets(prevAssets => 
      prevAssets.map(a => 
        a._id === asset._id 
          ? { ...a, showVideo: !a.showVideo }
          : a
      )
    );
  };

  const handleVideoEnd = (asset) => {
    setAssets(prevAssets => 
      prevAssets.map(a => 
        a._id === asset._id 
          ? { ...a, showVideo: false }
          : a
      )
    );
  };

  const handleWorkshopDate = async (planId) => {
    const dateInput = document.getElementById(`workshopDate${planId}`);
    if (!dateInput || !dateInput.value) {
      alert('Please select a date for the workshop');
      return;
    }

    try {
      const response = await axios.post(createApiUrl(`/api/add-to-basket/${planId}`), {
        workshopDate: dateInput.value
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        window.location.href = response.data.redirectUrl || '/basket';
      } else {
        alert(response.data.message || 'Failed to add workshop to basket');
      }
    } catch (error) {
      console.error('Error adding workshop to basket:', error);
      alert('Error adding workshop to basket. Please try again.');
    }
  };

  const toggleAutoRenew = async (subscriptionId, autoRenew) => {
    try {
      await axios.post(createApiUrl(`/api/update-auto-renew-status`), {
        subscriptionId: subscriptionId,
        renew: autoRenew
      }, {
        withCredentials: true
      });
      
      setSubscriptions(prev => {
        return prev.map(sub => 
          sub._id === subscriptionId ? { ...sub, autoRenew } : sub
        );
      });
    } catch (error) {
      console.error('Error updating auto-renew status:', error);
      alert('Failed to update auto-renew status');
    }
  };

  const handleRenewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRenewalModal(true);
  };

  const handleRenewalSuccess = () => {
    fetchData(); // Refresh the data after successful renewal
  };

  const handleCloseRenewalModal = () => {
    setShowRenewalModal(false);
    setSelectedSubscription(null);
  };

  return (
    <>
    <BaseDashboard
      title="User Dashboard"
      tabs={tabs}
      user={user}
      loading={loading}
          >
      <div tabId="overview" className="overview-tab-content">
        <div className="row">
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Active Subscriptions</h5>
                <p className="card-content">
                  <strong>Count:</strong> {subscriptions.filter(sub => sub.status === 'active').length}<br/>
                  <strong>Total:</strong> {subscriptions.length}
                </p>
                <button className="btn btn-primary">View All</button>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Available Lessons</h5>
                <p className="card-content">
                  <strong>Count:</strong> {assets.length}<br/>
                  <strong>Categories:</strong> {new Set(assets.map(asset => asset.category)).size}
                </p>
                <button className="btn btn-primary">Browse Lessons</button>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card">
                      <div className="card-body">
                <h5 className="card-title">Workshop Plans</h5>
                <p className="card-content">
                  <strong>Available:</strong> {workshopPlans.length}<br/>
                  <strong>Booked:</strong> {subscriptions.filter(sub => sub.planType === 'workshop').length}
                </p>
                <button className="btn btn-primary">View Workshops</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons Tab */}
      <div tabId="lessons" className="lessons-tab-content">
        <div className="lessons-grid">
          {assets.map((asset) => (
            <div key={asset._id} className="content-card">
              <h5 className="card-title">{asset.name}</h5>
              <p className="card-content">{asset.description}</p>
                        
                        {/* Video container */}
                        <div className="video-container">
                {asset.video && asset.video.thumbnail && !asset.showVideo && (
                            <img 
                    src={asset.video.thumbnail} 
                    alt={`Thumbnail for ${asset.name}`} 
                    className="video-thumbnail" 
                    onClick={() => handleVideoClick(asset)}
                    style={{ cursor: 'pointer', width: '100%', height: '200px', objectFit: 'cover' }}
                            />
                          )}
                          
                {asset.file && asset.file.url && asset.showVideo && (
                            <video
                              controls
                              {...restrictedVideoProps}
                              autoPlay
                              className="video-player"
                              onEnded={() => handleVideoEnd(asset)}
                              style={{ width: '100%', height: '200px' }}
                            >
                    <source src={asset.file.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )}
                        </div>
                        
                        {/* Teacher guides */}
              {asset.documents && asset.documents.length > 0 && (
                <div className="teacher-guides">
                  <h6>Teacher Guides:</h6>
                  <div className="guide-links">
                    {asset.documents.map((document, docIndex) => (
                        <a key={docIndex} href={document.url || document.documentUrl} target="_blank" rel="noopener noreferrer" className="guide-link">
                                  <i className="fas fa-file-pdf"></i> {assetFileDisplayName(document) || 'Document'}
                                </a>
                            ))}
                      </div>
                    </div>
              )}
                  </div>
                ))}
              </div>
          </div>

        {/* Workshops Tab */}
      <div tabId="workshops" className="workshops-tab-content">
              <div className="row">
          {workshopPlans.map((plan) => (
            <div key={plan._id} className="col-md-4">
                    <div className="card">
                      <div className="card-body">
                  <h5 className="card-title">{plan.name}</h5>
                  <p className="card-content">
                    <strong>Duration:</strong> {plan.duration < 30 ? `${plan.duration} days` : plan.duration === 365 ? '1 year' : `${Math.ceil(plan.duration / 30)} months`}<br/>
                    <strong>Price:</strong> £{plan.price}<br/>
                    <strong>Description:</strong> {plan.description}
                  </p>
                          
                          {plan.type === 'workshop' && (
                    <div className="workshop-date">
                      <label htmlFor={`workshopDate${plan._id}`}>Select Date:</label>
                              <input 
                                type="date" 
                                id={`workshopDate${plan._id}`} 
                                name="workshopDate" 
                                className="form-control"
                              />
                            </div>
                          )}
                  
                            <button 
                    className="btn btn-primary w-100" 
                              onClick={() => handleWorkshopDate(plan._id)}
                            >
                              Buy Workshop
                            </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>

        {/* Subscriptions Tab */}
      <div tabId="subscriptions" className="subscriptions-tab-content">
        <div className="row">
          {subscriptions.map((subscription) => (
            <div key={subscription._id} className="col-md-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">{subscription.planName}</h5>
                  <h6 className="card-subtitle mb-2 text-muted">Status: {subscription.status}</h6>
                  
                  {subscription.planType === 'workshop' ? (
                    <p className="card-content">
                      <strong>Purchase Date:</strong> {new Date(subscription.startDate).toLocaleDateString()}<br/>
                      <strong>Workshop Date:</strong> {new Date(subscription.workshopDate).toLocaleDateString()}
                    </p>
                  ) : (
                    <p className="card-content">
                      <strong>Start Date:</strong> {new Date(subscription.startDate).toLocaleDateString()}<br/>
                      <strong>End Date:</strong> {new Date(subscription.endDate).toLocaleDateString()}
                    </p>
                  )}
                      
                  {subscription.status !== 'expired' && subscription.status !== 'cancelled' && subscription.planType !== 'workshop' && (
                    <div className="subscription-controls">
                      <div className="auto-renew-control">
                          <input
                          className="form-check-input"
                            type="checkbox"
                            id={`autoRenew${subscription._id}`}
                            checked={subscription.autoRenew}
                            onChange={(e) => toggleAutoRenew(subscription._id, e.target.checked)}
                          />
                        <label className="form-check-label" htmlFor={`autoRenew${subscription._id}`}>
                          <strong>Auto Renew</strong>
                          </label>
                      </div>

                      {!subscription.autoRenew && (() => {
                        const endDate = new Date(subscription.endDate);
                        const currentDate = new Date();
                        const thirtyDaysFromNow = new Date(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));
                        const shouldShowRenew = endDate <= thirtyDaysFromNow;
                        return shouldShowRenew;
                      })() && (
                                <button 
                                  className="btn btn-primary" 
                                  onClick={() => handleRenewSubscription(subscription)}
                        >
                          {subscription.planName === '30-day FREE trial' ? 'Upgrade Subscription' : 'Renew Subscription'}
                                </button>
                      )}
                          </div>
                  )}
                  
                  {subscription.documentUrl && (
                    <a href={subscription.documentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-success">
                      View Invoice/Receipt
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
          </div>

        {/* AI Agent Tab */}
      <div tabId="aiagent" className="aiagent-tab-content">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">KBH AI Agent</h5>
                <p className="card-content">
                  Our Black History AI Agent is here to help you! Ask questions, get assistance with lessons, and explore historical content.
                </p>
                <div className="ai-agent-container">
                  <div id="messages" className="ai-messages"></div>
                  
                  <form id="aiAgentForm" encType="multipart/form-data" className="ai-form">
                  <textarea 
                    id="userInput" 
                    name="message" 
                    placeholder="Type your question..." 
                      className="form-control"
                  />
                  
                    <div className="ai-controls">
                      <DragDropFileUpload
                        onFileSelected={handleFileUpload}
                        onFileRemoved={handleFileRemove}
                        selectedFile={uploadedFiles.length > 0 ? uploadedFiles[0] : null}
                        accept=".txt,.pdf,.docx"
                        multiple={true}
                        maxFileSize={10 * 1024 * 1024} // 10MB
                        supportedTypes={['.txt', '.pdf', '.docx']}
                        placeholder="Click to upload files or drag and drop"
                        description="Supports: TXT, PDF, DOCX files"
                      />
                      
                      <div className="ai-buttons">
                      <button type="button" className="btn btn-primary">Submit</button>
                      <button type="button" className="btn btn-secondary">🎤 Speak</button>
                      <button id="readingButton" className="btn btn-danger">Read</button>
                    </div>
                  </div>
                </form>
                </div>
              </div>
            </div>
          </div>
        </div>
              </div>
      </BaseDashboard>
      
      <RenewalModal
        isOpen={showRenewalModal}
        onClose={handleCloseRenewalModal}
        currentSubscription={selectedSubscription}
        onRenewalSuccess={handleRenewalSuccess}
      />
    </>
  );
};

  export default UserDashboard;
  